import "./style.scss";
import { useEffect, useState } from "react";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import SearchSvg from "@/src/svg/CustomUtils/SearchSvg";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import CheckboxActiveSvg from "@/src/svg/CustomUtils/CheckboxActiveSvg";
import CheckboxNonActiveSvg from "@/src/svg/CustomUtils/CheckboxNonActiveSvg";

export type CustomMultiSelectItem = {
  id: number;
  title: string;
  cost: string;
  usd: string;
};

type Props = {
  title: string;
  items: CustomMultiSelectItem[];
  isSearch?: boolean;
  initialSelectedTitles?: string;
  onClose: () => void;
  onApply?: (selectedItems: CustomMultiSelectItem[]) => void;
};

const CustomMultiSelect = ({ title, items, isSearch = false, initialSelectedTitles, onClose, onApply }: Props) => {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const filteredItems = isSearch && search.trim() ? items.filter((item) => item.title.toLowerCase().includes(search.toLowerCase())) : items;

  useEffect(() => {
    if (!initialSelectedTitles) return;
    const titles = initialSelectedTitles
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const ids = items.filter((item) => titles.includes(item.title)).map((item) => item.id);
    setSelectedIds(ids);
  }, [initialSelectedTitles, items]);

  const handleCancel = () => {
    if (isSearch) setSearch("");
    const selectedItems = items.filter((item) => selectedIds.includes(item.id));
    if (onApply) {
      onApply(selectedItems);
    }
    onClose();
  };

  const handleToggleItem = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]));
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleSelectAll = () => {
    setSelectedIds(items.map((item) => item.id));
  };

  return (
    <div className="custom-multi-select">
      <div className="custom-multi-select-title">
        <div className="custom-multi-select-title-text">
          <p>{title}</p>
        </div>
        <div className="custom-multi-select-title-close" onClick={handleCancel}>
          <CloseSvg />
        </div>
      </div>
      {isSearch && (
        <div className="custom-multi-select-search">
          <div className="custom-multi-select-search-svg">
            <SearchSvg />
          </div>
          <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      )}
      <ul className="custom-multi-select-ul">
        {filteredItems.map((item) => (
          <li key={item.id} className="custom-multi-select-ul-li" onClick={() => handleToggleItem(item.id)}>
            <div className="custom-multi-select-ul-li-title">
              <p>{item.title}</p>
            </div>
            <div className="custom-multi-select-ul-li-content">
              <div className="custom-multi-select-ul-li-content-cost">
                <p>{item.cost}</p>
              </div>
              <div className="custom-multi-select-ul-li-content-usd">
                <p>{item.usd}</p>
              </div>
            </div>
            <div className="custom-multi-select-ul-li-checkbox">
              {selectedIds.includes(item.id) ? <CheckboxActiveSvg /> : <CheckboxNonActiveSvg />}
            </div>
          </li>
        ))}
      </ul>
      <div className="custom-multi-select-buttons">
        <BtnDefault onClick={handleClearSelection}>
          <p>Clear</p>
        </BtnDefault>
        <BtnDefault onClick={handleSelectAll}>
          <p>Choose All</p>
        </BtnDefault>
      </div>
    </div>
  );
};

export default CustomMultiSelect;
